import * as togeojson from "@mapbox/togeojson";
import { bearing } from "@turf/bearing";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { distance } from "@turf/distance";
import { feature, lineString } from "@turf/helpers";
import { pointToLineDistance } from "@turf/point-to-line-distance";
import type {
    Feature,
    FeatureCollection,
    Geometry,
    MultiPolygon,
    Point,
    Polygon,
    Position,
} from "geojson";
import type { Site } from "../apiClient/generated";

export const emptyFeatureCollection: FeatureCollection = {
    type: "FeatureCollection",
    features: [],
};

interface readGeoFileAsJson {
    file?: File;
    callback: (geojson: FeatureCollection | undefined, error: boolean) => void;
}

export const readGeoFileAsGeoJson = (props: readGeoFileAsJson) => {
    if (props.file) {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                // Try parsing as GeoJSON
                const jsonData = normalizeGEOJSON(
                    JSON.parse(e.target!.result as string),
                );
                props.callback(jsonData, false);
            } catch {
                // Try parsing as KML
                try {
                    const kml = new DOMParser().parseFromString(
                        e.target!.result as string,
                        "application/xml",
                    );
                    const errorNode = kml.querySelector("parsererror");
                    if (errorNode) {
                        throw new Error(
                            "Error while trying to parse file as KML.",
                        );
                    } else {
                        const jsonData = normalizeGEOJSON(togeojson.kml(kml));
                        props.callback(jsonData, false);
                    }
                } catch {
                    props.callback(undefined, true);
                }
            }
        };

        reader.readAsText(props.file);
    }
};

export const normalizeGEOJSON = (json: any) => {
    if (json.type === "FeatureCollection") {
        return json;
    } else if (json.type === "Feature") {
        return {
            type: "FeatureCollection",
            features: [json],
        };
    }
    return {
        type: "FeatureCollection",
        features: [
            {
                type: "Feature",
                geometry: json,
            },
        ],
    };
};

type FeatureInput = {
    [key: string]: any;
    shape?: any;
    geometry?: any;
    location?: any;
};

export const createFeatureCollection = (
    data: FeatureInput[],
): FeatureCollection => {
    const getShape = (feature: FeatureInput) => {
        if (feature.shape) {
            return feature.shape;
        }
        if (feature.geometry) {
            return feature.geometry;
        }
        if (feature.location) {
            return feature.location;
        }
    };
    const features = [];
    for (const i of data) {
        if (i.geometry?.type === "GeometryCollection") {
            i.geometry.geometries.forEach((g: Geometry) => {
                features.push({
                    type: "Feature",
                    properties: i,
                    geometry: g,
                });
            });
        } else {
            features.push({
                type: "Feature",
                properties: i,
                geometry: getShape(i),
            });
        }
    }

    return {
        type: "FeatureCollection",
        features: features as Feature[],
    };
};

export const isValidCoordinates = (lat: number, lon: number) => {
    if (Number.isNaN(lon) || Number.isNaN(lat)) {
        return false;
    }
    return -180 <= lon && lon <= 180 && -90 <= lat && lat <= 90;
};

/**
 * Distance in meters, measured to the site's shape when it has one and to its
 * center point otherwise.
 */
export const distanceToSite = (site: Site, point: Point): number | null => {
    if (site.shape) {
        return customDistanceToPolygon(site.shape as Polygon, feature(point));
    }
    if (site.location) {
        return distance(point, site.location as Point, { units: "meters" });
    }
    return null;
};

export const findClosestSite = (sites: Site[], point: Point): Site | null => {
    if (!sites?.length || !point) return null;

    let closestSite: Site | null = null;
    let smallestDistance: number | null = null;

    for (const site of sites) {
        const d = distanceToSite(site, point);
        if (d !== null && (smallestDistance === null || d < smallestDistance)) {
            smallestDistance = d;
            closestSite = site;
        }
    }

    return closestSite;
};

export const customDistanceToPolygon = (
    polygon: Polygon | MultiPolygon,
    point: Feature<Point>,
): number => {
    let smallestDistance = Number.MAX_VALUE;

    if (booleanPointInPolygon(point, polygon)) {
        return 0;
    }

    // Handle both Polygon and MultiPolygon types
    let polygons: Position[][][];
    if (polygon.type === "MultiPolygon") {
        polygons = polygon.coordinates;
    } else if (polygon.type === "Polygon") {
        polygons = [polygon.coordinates];
    }

    if (!polygons) {
        return Infinity;
    }

    for (const innerPolygon of polygons) {
        const outer = innerPolygon[0];
        const outerLine = lineString(outer);
        const distance = pointToLineDistance(point, outerLine, {
            units: "meters",
        });

        if (distance < smallestDistance) {
            smallestDistance = distance;
        }
    }

    return smallestDistance;
};

export const formatDistance = (meters?: number | null): string => {
    if (meters === null || meters === undefined || Number.isNaN(meters)) {
        return "-";
    }
    if (meters < 1000) {
        return `${Math.round(meters)} m`;
    }
    return `${(meters / 1000).toFixed(1)} km`;
};

const COMPASS_POINTS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

/**
 * 8-point compass bearing, e.g. "NE".
 */
export const compassBearing = (from: Point, to: Point): string => {
    if (!from?.coordinates || !to?.coordinates) {
        return "";
    }
    // `bearing` returns -180..180.
    const degrees = (bearing(from, to) + 360) % 360;

    return COMPASS_POINTS[Math.round(degrees / 45) % 8];
};
